import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { BookOpen, Download } from "lucide-react";

type DigitalProduct = Pick<
  Tables<"digital_products">,
  "id" | "slug" | "title" | "short_description" | "cover_image_url" | "price_cents" | "currency" | "is_active" | "is_featured" | "sort_order"
>;

const formatPrice = (cents: number, currency: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);

const FeaturedDigitalLibrary = () => {
  const [products, setProducts] = useState<DigitalProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await (supabase as any)
        .from("digital_products_public")
        .select("id, slug, title, short_description, cover_image_url, price_cents, currency, is_active, is_featured, sort_order")
        .eq("is_active", true)
        .eq("is_featured", true)
        .order("sort_order", { ascending: true })
        .limit(4);
      setProducts(data || []);
      setLoading(false);
    })();
  }, []);

  if (loading || products.length === 0) return null;

  return (
    <section className="py-16 bg-secondary/30">
      <div className="container mx-auto px-6">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <BookOpen className="w-8 h-8 text-primary mx-auto mb-3" />
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-3">
            Featured Digital Library
          </h2>
          <p className="text-foreground/70 font-sans">
            Guided journals, vision workbooks, and Caribbean wellness reads from AnaElli Digital — instant download after purchase.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product) => (
            <Link
              key={product.id}
              to={`/brands/anaelli#${product.slug}`}
              className="group bg-background rounded-lg overflow-hidden border border-border hover:shadow-lg transition-shadow"
            >
              <div className="aspect-[3/4] bg-muted overflow-hidden">
                {product.cover_image_url ? (
                  <img
                    src={product.cover_image_url}
                    alt={product.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <BookOpen className="w-12 h-12" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-serif text-lg text-foreground mb-1 line-clamp-2">
                  {product.title}
                </h3>
                {product.short_description && (
                  <p className="text-sm text-muted-foreground font-sans mb-3 line-clamp-2">
                    {product.short_description}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <span className="font-sans font-semibold text-primary">
                    {formatPrice(product.price_cents, product.currency)}
                  </span>
                  <span className="text-xs font-sans uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                    <Download className="w-3 h-3" /> Instant
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/brands/anaelli"
            className="inline-block font-sans text-sm uppercase tracking-widest text-primary hover:underline"
          >
            Browse all digital titles →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedDigitalLibrary;
